import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Crosshair } from "lucide-react";
import { ChapterHeading } from "./ChapterHeading";
import { getIcon, matchConcepts, tagLabel, INDIVIDUAL } from "../lib/concepts";
import { useLang } from "../lib/i18n";

const selectCls = "w-full bg-transparent border border-ink/25 focus:border-gold outline-none px-5 py-4 text-sm text-ink transition-colors duration-300 cursor-pointer rtl:bg-left";

export const Matcher = ({ concepts, sectors, tags, onUseConcept }) => {
  const [sector, setSector] = useState("");
  const [tag, setTag] = useState("");
  const [results, setResults] = useState(null);
  const { t, tf, sectorName, tagL } = useLang();

  const run = () => setResults(matchConcepts(concepts, sector || "any", tag || "any"));
  const individualLabel = tf("matcher.businessIn", { s: "" }).trim().startsWith("شركة") ? "فرد / مشروع شخصي" : INDIVIDUAL;

  return (
    <section id="matcher" data-testid="matcher-section" className="bg-cream py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <ChapterHeading number="01" title={t("matcher.title")} />

        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}>
            <p className="text-ink/70 text-sm md:text-base leading-relaxed max-w-md mb-10">
              {t("matcher.intro")}
            </p>

            <div className="space-y-6">
              <div>
                <label htmlFor="matcher-sector" className="block text-[11px] tracking-[0.3em] uppercase text-green/60 mb-3">{t("matcher.iam")}</label>
                <select id="matcher-sector" data-testid="matcher-sector-select" value={sector} onChange={(e) => setSector(e.target.value)} className={selectCls}>
                  <option value="">{t("matcher.chooseSector")}</option>
                  <option value={INDIVIDUAL}>{individualLabel}</option>
                  {sectors.map((s) => <option key={s} value={s}>{tf("matcher.businessIn", { s: sectorName(s) })}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="matcher-tag" className="block text-[11px] tracking-[0.3em] uppercase text-green/60 mb-3">{t("matcher.drag")}</label>
                <select id="matcher-tag" data-testid="matcher-tag-select" value={tag} onChange={(e) => setTag(e.target.value)} className={selectCls}>
                  <option value="">{t("matcher.chooseProblem")}</option>
                  {tags.map((tg) => <option key={tg} value={tg}>{tagL(tg, tagLabel(tg))}</option>)}
                </select>
              </div>
              <button data-testid="matcher-run-btn" onClick={run} disabled={!sector && !tag}
                className="group flex items-center gap-3 bg-ink text-cream text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-green disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300">
                <Crosshair size={14} className="text-gold" />
                {t("matcher.run")}
              </button>
            </div>
          </motion.div>

          <div data-testid="matcher-results" className="min-h-[320px]">
            <AnimatePresence mode="wait">
              {results === null ? (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="h-full min-h-[320px] border border-dashed border-ink/25 grid place-items-center p-10">
                  <p className="font-display italic text-ink/40 text-xl text-center">{t("matcher.empty")}</p>
                </motion.div>
              ) : results.length === 0 ? (
                <motion.div key="none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="h-full min-h-[320px] border border-dashed border-ink/25 grid place-items-center p-10">
                  <p className="font-display italic text-ink/40 text-xl text-center">{t("matcher.none")}</p>
                </motion.div>
              ) : (
                <motion.div key="list" initial="hidden" animate="show" exit={{ opacity: 0 }}
                  variants={{ show: { transition: { staggerChildren: 0.12 } } }} className="space-y-4">
                  {results.map((c, i) => {
                    const Icon = getIcon(c.icon);
                    return (
                      <motion.article key={c.ref} data-testid={`matcher-result-${i}`}
                        variants={{ hidden: { opacity: 0, x: 32 }, show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } }}
                        className="group border border-ink/20 bg-cream p-6 md:p-7 flex gap-5 items-start hover:border-gold transition-colors duration-300">
                        <span className="font-display text-gold text-2xl leading-none mt-1">0{i + 1}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap mb-2">
                            <Icon size={16} className="text-green" />
                            <span className="text-[10px] tracking-[0.3em] uppercase text-ink/50">{c.ref} · {sectorName(c.sector)}</span>
                            {c.confirmed && <span className="text-[9px] tracking-[0.25em] uppercase bg-gold text-ink px-2 py-0.5">{t("library.confirmed")}</span>}
                          </div>
                          <h3 className="font-display text-xl text-ink">{c.title}</h3>
                          <p className="mt-2 text-sm text-ink/60 leading-relaxed">{c.promise}</p>
                          <button data-testid={`matcher-use-btn-${i}`} onClick={() => onUseConcept(c)}
                            className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-green underline underline-offset-4 decoration-gold hover:text-ink transition-colors duration-300">
                            {t("overlay.use")} <ArrowUpRight size={13} className="rtl:-scale-x-100" />
                          </button>
                        </div>
                      </motion.article>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
